import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Download, AlertTriangle, Filter, X, Plus, Trash2, CheckCircle2, History } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import SaveIndicator from "./SaveIndicator";
import SavedFiltersBar from "./SavedFiltersBar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ProcessedFile } from "./InvoiceList";
import { computeSeriesNumbers } from "@/lib/invoiceSeries";

import {
  loadSupplierMaster,
  type CompanyMaster,
  type SupplierMasterEntry,
} from "@/lib/supplierMaster";
import {
  loadAccountHistory,
  recordAccountUsage,
  type AccountHistoryEntry,
} from "@/lib/accountHistory";
import { supabase } from "@/integrations/supabase/client";
import ExpenseAccountCombobox, {
  type SuggestionEntry,
  type CatalogEntry,
} from "./ExpenseAccountCombobox";
import { loadCompanyAccountCatalog } from "@/lib/companyAccountCatalog";
import { expandAccountShortcut, inferAccountLength } from "@/lib/accountShortcut";
import QuickSupplierDialog, { type QuickSupplierCreated } from "./QuickSupplierDialog";
import ExcelJS from "exceljs";
import { formatNumber, parseNumericLike } from "@/lib/numberFormatting";
import ExportBatchesDialog from "./ExportBatchesDialog";
import { useToast } from "@/hooks/use-toast";
import { insertHistoryEntries } from "@/lib/invoiceHistory";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyExportSettings } from "@/hooks/useCompanyExportSettings";

interface VatBookRow {
  ivaSeriesNumber: string;
  date: string;
  supplier: string;
  supplierTaxId: string;
  invoiceNumber: string;
  taxableBase: number;
  vatRate: number;
  vatAmount: number;
  irpfRate: number | null;
  irpfAmount: number | null;
  totalAmount: number | null;
  paymentMethod: string;
  supplierAccountCode: string;
  expenseAccountCode: string;
  supplierDescription: string;
  fileId: string;
  vatLineIndex: number;
  totalInconsistency: boolean;
  calculatedTotal: number | undefined;
  leasingCostRecovery: number | null;
  leasingFinancialCharge: number | null;
  leasingInsurance: number | null;
  leasingCostRecoveryAccount: string;
  leasingFinancialChargeAccount: string;
  leasingInsuranceAccount: string;
  licensePlate: string;
  postalCode: string;
}

interface VatBookProps {
  files: ProcessedFile[];
  prefix: string;
  startNumber: number;
  appliedCompany?: CompanyMaster | null;
  saveStatus?: import("@/hooks/useInvoiceSession").SaveStatus;
  lastSavedAt?: number | null;
  onUpdateExpenseAccount?: (fileId: string, value: string, vatLineIndex?: number) => void;
  onUpdateSupplierAccount?: (fileId: string, value: string) => void;
  onUpdateVatBookField?: (
    fileId: string,
    field: "date" | "supplier" | "supplierTaxId" | "invoiceNumber" | "paymentMethod",
    value: string
  ) => void;
  onUpdateNumericField?: (
    fileId: string,
    field: "taxableBase" | "vatRate" | "vatAmount" | "irpfRate" | "irpfAmount" | "totalAmount",
    value: number | string,
    vatLineIndex?: number
  ) => void;
  onUpdateDescription?: (fileId: string, value: string) => void;
  onApplyMaster?: (company: CompanyMaster) => void;
  onAddVatLine?: (fileId: string) => void;
  onRemoveVatLine?: (fileId: string, vatLineIndex: number) => void;
}

const normalizeNif = (nif: string): string =>
  nif.replace(/[\s.\-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");

const normalizePlate = (p: string): string =>
  p.replace(/[\s\-]/g, "").toUpperCase();

const safeText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeInvoiceNumber = (value: unknown): string => {
  // Mismo criterio que la exportación SUENLACE: se respeta el número de factura
  // del proveedor tal cual (sin recortar a los últimos dígitos).
  return safeText(value).toUpperCase();
};

const buildSuggestions = (
  taxId: string,
  licensePlate: string | undefined,
  master: Map<string, SupplierMasterEntry> | null,
  catalog: CatalogEntry[],
): SuggestionEntry[] => {
  if (!master || !taxId) return [];
  const entry = master.get(normalizeNif(taxId));
  if (!entry) return [];

  const out: SuggestionEntry[] = [];
  const seen = new Set<string>();
  const plate = licensePlate ? normalizePlate(licensePlate) : "";

  // Match by license plate or contract number against catalog descriptions
  // (Only useful for suppliers like Transvanadis that have contract numbers in descriptions)
  if (plate) {
    for (const cat of catalog) {
      if (seen.has(cat.account)) continue;
      const desc = (cat.description || "").toUpperCase();
      if (desc.includes(plate)) {
        out.push({
          account: cat.account,
          description: cat.description,
          matchesPlate: true,
        });
        seen.add(cat.account);
      }
    }
  }

  // Include supplier's default account from master if exists
  if (entry.accountCode && !seen.has(entry.accountCode)) {
    // Find description from catalog, fallback to master description
    const catalogMatch = catalog.find(c => c.account === entry.accountCode);
    out.push({
      account: entry.accountCode,
      description: catalogMatch?.description ?? entry.description,
      matchesPlate: false,
    });
    seen.add(entry.accountCode);
  }

  return out;
};

const buildRows = (files: ProcessedFile[], prefix: string, startNumber: number): VatBookRow[] => {
  const sorted = [...files].filter(Boolean).sort((a, b) => safeText(a?.date).localeCompare(safeText(b?.date)));
  const rows: VatBookRow[] = [];
  const seriesNumbers = computeSeriesNumbers(sorted, prefix, startNumber);

  sorted.forEach((f, idx) => {
    const seriesNum = seriesNumbers.get(f.id) ?? `${safeText(prefix).toUpperCase()}${startNumber + idx}`;
    const breakdown = Array.isArray(f.vatBreakdown) && f.vatBreakdown.length > 0
      ? f.vatBreakdown
      : [{ taxableBase: 0, vatRate: 0, vatAmount: 0 }];

    breakdown.forEach((vat, vatIdx) => {
      // Per-line expense account: use the line's own value if set, else fall back to the file-level
      // expenseAccountCode ONLY for the first line (line 0). Additional lines start empty so the user
      // can assign a different counterpart per VAT line.
      const lineExpense = (vat as { expenseAccount?: string }).expenseAccount;
      const expenseForRow = typeof lineExpense === "string"
        ? lineExpense
        : vatIdx === 0
          ? (f.expenseAccountCode || "")
          : "";
      rows.push({
        ivaSeriesNumber: seriesNum,
        date: safeText(f.date),
        supplier: safeText(f.supplier),
        supplierTaxId: normalizeNif(f.supplierTaxId || ""),
        invoiceNumber: normalizeInvoiceNumber(f.invoiceNumber),
        taxableBase: parseNumericLike(vat?.taxableBase),
        vatRate: parseNumericLike(vat?.vatRate),
        vatAmount: parseNumericLike(vat?.vatAmount),
        irpfRate: vatIdx === 0 ? parseNumericLike(f.irpfRate ?? 0) : null,
        irpfAmount: vatIdx === 0 ? parseNumericLike(f.irpfAmount ?? 0) : null,
        totalAmount: vatIdx === 0 ? parseNumericLike(f.totalAmount ?? 0) : null,
        paymentMethod: vatIdx === 0 ? (f.paymentMethod || "") : "",
        supplierAccountCode: f.supplierAccountCode || "",
        expenseAccountCode: expenseForRow,
        supplierDescription: f.supplierDescription || "",
        fileId: f.id,
        vatLineIndex: vatIdx,
        totalInconsistency: vatIdx === 0 ? (f.totalInconsistency ?? false) : false,
        calculatedTotal: vatIdx === 0 ? parseNumericLike(f.calculatedTotal) : undefined,
        leasingCostRecovery: vatIdx === 0 ? (f.leasingCostRecovery ?? null) : null,
        leasingFinancialCharge: vatIdx === 0 ? (f.leasingFinancialCharge ?? null) : null,
        leasingInsurance: vatIdx === 0 ? (f.leasingInsurance ?? null) : null,
        leasingCostRecoveryAccount: f.leasingCostRecoveryAccount || "",
        leasingFinancialChargeAccount: f.leasingFinancialChargeAccount || "",
        leasingInsuranceAccount: f.leasingInsuranceAccount || "",
        licensePlate: f.licensePlate || "",
        postalCode: f.postalCode || "",
      });
    });
  });

  return rows;
};

const fmt = (n: unknown) => formatNumber(n);

const buildVatRowKey = (row: VatBookRow) => [
  row.fileId,
  row.ivaSeriesNumber,
  row.invoiceNumber,
  row.taxableBase,
  row.vatRate,
  row.vatAmount,
  row.irpfRate ?? "",
  row.irpfAmount ?? "",
  row.totalAmount ?? "",
  row.paymentMethod,
].join(":");

type EditableField =
  | "date"
  | "supplier"
  | "supplierTaxId"
  | "invoiceNumber"
  | "paymentMethod"
  | "supplierAccount"
  | "expenseAccount"
  | "taxableBase"
  | "vatRate"
  | "vatAmount"
  | "irpfRate"
  | "irpfAmount"
  | "totalAmount"
  | "supplierDescription";

const VatBook = ({
  files,
  prefix,
  startNumber,
  appliedCompany,
  saveStatus,
  lastSavedAt,
  onUpdateExpenseAccount,
  onUpdateSupplierAccount,
  onUpdateVatBookField,
  onUpdateNumericField,
  onUpdateDescription,
  onApplyMaster,
  onAddVatLine,
  onRemoveVatLine,
}: VatBookProps) => {
  const { toast } = useToast();
  const orgExportSettings = useCompanyExportSettings(appliedCompany?.organization_id ?? null);
  const rows = useMemo(() => buildRows(files, prefix, startNumber), [files, prefix, startNumber]);
   const [editingCell, setEditingCell] = useState<string | null>(null);
   const [editField, setEditField] = useState<EditableField>("expenseAccount");
   const [editValue, setEditValue] = useState("");
   const [editingFileId, setEditingFileId] = useState<string | null>(null);
   const [editingVatLineIndex, setEditingVatLineIndex] = useState<number>(0);
   
   const [isMounted, setIsMounted] = useState(false);
   const [master, setMaster] = useState<Map<string, SupplierMasterEntry> | null>(null);
   const [history, setHistory] = useState<Map<string, AccountHistoryEntry[]>>(new Map());
    const [companyCatalog, setCompanyCatalog] = useState<CatalogEntry[]>([]);
    const [accountingDate, setAccountingDate] = useState<string>("");
    const [archivosDialogOpen, setArchivosDialogOpen] = useState(false);
    const [highlightedFileId, setHighlightedFileId] = useState<string | null>(null);
    const [pendingHistory, setPendingHistory] = useState<{ entries: any[]; source: "excel" | "a3" } | null>(null);
    /** Contexto del diálogo de alta rápida de proveedor desde el botón junto al NIF. */
    const [quickSupplierCtx, setQuickSupplierCtx] = useState<{ fileId: string; taxId: string; supplier: string } | null>(null);

    // Memory of corrections (Entrega 3.3): in-session counter of manual edits to expense
    // account per (taxId, account). Once a pair reaches >= MEMORIZE_THRESHOLD,
    // we prompt the user to memorize the choice for the supplier.
    const correctionCountsRef = useRef<Map<string, number>>(new Map());
    const promptedPairsRef = useRef<Set<string>>(new Set());
    const MEMORIZE_THRESHOLD = 2;

   useEffect(() => {
     setIsMounted(true);
   }, []);

   // Load master + history + company catalog when applied company changes
   useEffect(() => {
     let cancelled = false;
     (async () => {
       try {
         const [m, h, cat] = await Promise.all([
           loadSupplierMaster(appliedCompany?.id),
           loadAccountHistory(appliedCompany?.id ?? null),
           appliedCompany?.id ? loadCompanyAccountCatalog(appliedCompany.id) : Promise.resolve([]),
         ]);
         if (!cancelled) {
           setMaster(m);
           setHistory(h);
           setCompanyCatalog(
             (cat as { account: string; description: string }[]).map((c) => ({
               account: c.account,
               description: c.description,
             })),
           );
         }
       } catch (e) {
         console.warn("[VatBook] master/history/catalog load failed:", e);
       }
     })();
     return () => { cancelled = true; };
   }, [appliedCompany?.id]);

  // Catalog: SOLO entradas de company_account_catalog (Contrapartidas) de la empresa activa.
  // No mezclar con cuentas derivadas del maestro de proveedores.
  const catalog = useMemo<CatalogEntry[]>(() => {
    const seen = new Map<string, string>();
    companyCatalog.forEach((c) => {
      const a = (c.account || "").trim();
      if (!a || seen.has(a)) return;
      seen.set(a, c.description || "");
    });
    return Array.from(seen.entries())
      .map(([account, description]) => ({ account, description }))
      .sort((a, b) => a.account.localeCompare(b.account));
  }, [companyCatalog]);

   // Per-column filters: each key maps to a Set of selected values (null = show all)
   type ColKey = "ivaSeriesNumber" | "date" | "supplier" | "supplierTaxId" | "invoiceNumber" | "taxableBase" | "vatRate" | "vatAmount" | "irpfRate" | "irpfAmount" | "totalAmount" | "paymentMethod" | "supplierAccountCode" | "expenseAccountCode" | "supplierDescription" | "leasingCostRecovery" | "leasingCostRecoveryAccount" | "leasingFinancialCharge" | "leasingFinancialChargeAccount" | "leasingInsurance" | "leasingInsuranceAccount";
   const [columnFilters, setColumnFilters] = useState<Partial<Record<ColKey, Set<string>>>>({});
   const [filterSearch, setFilterSearch] = useState<Record<string, string>>({});

   const EMPTY_LABEL = "(Vacío)";
   const EMPTY_VALUE = "__EMPTY__";

   const getColValue = (r: VatBookRow, col: ColKey): string => {
     const v = r[col];
     if (v === null || v === undefined || v === "") return "";
     if (typeof v === "number") return fmt(v);
     return String(v);
   };

   const uniqueValuesForCol = useMemo(() => {
     const map: Partial<Record<ColKey, string[]>> = {};
      const cols: ColKey[] = ["ivaSeriesNumber","date","supplier","supplierTaxId","invoiceNumber","taxableBase","vatRate","vatAmount","irpfRate","irpfAmount","totalAmount","paymentMethod","supplierAccountCode","expenseAccountCode","supplierDescription","leasingCostRecovery","leasingCostRecoveryAccount","leasingFinancialCharge","leasingFinancialChargeAccount","leasingInsurance","leasingInsuranceAccount"];
     cols.forEach((col) => {
       const seen = new Set<string>();
       let hasEmpty = false;
       rows.forEach((r) => {
         const v = getColValue(r, col);
         if (v === "") hasEmpty = true;
         else seen.add(v);
       });
       const sorted = Array.from(seen).sort((a, b) => a.localeCompare(b, "es"));
       if (hasEmpty) sorted.unshift(EMPTY_VALUE);
       map[col] = sorted;
     });
     return map;
   }, [rows]);

   /**
    * Recurrent suppliers (Entrega 3.2): a supplier (NIF) is "recurrent" if it
    * has invoices in at least 3 distinct calendar months among the loaded
    * processed files. We compute on the full file list so the badge is stable
    * regardless of column filters.
    */
   const recurrentTaxIds = useMemo(() => {
     const monthsByTaxId = new Map<string, Set<string>>();
     for (const f of files) {
       const tid = normalizeNif(f.supplierTaxId || "");
       if (!tid || !f.date) continue;
       // f.date is yyyy-mm-dd; take yyyy-mm as month bucket.
       const month = f.date.slice(0, 7);
       if (!month || month.length !== 7) continue;
       const set = monthsByTaxId.get(tid) || new Set<string>();
       set.add(month);
       monthsByTaxId.set(tid, set);
     }
     const out = new Set<string>();
     for (const [tid, set] of monthsByTaxId) {
       if (set.size >= 3) out.add(tid);
     }
     return out;
   }, [files]);

   const [onlyRecurrent, setOnlyRecurrent] = useState(false);
   // Entrega 4.2 — filtros adicionales aplicables vía presets / filtros guardados.
   const [onlyWithIrpf, setOnlyWithIrpf] = useState(false);
   const [onlyCurrentQuarter, setOnlyCurrentQuarter] = useState(false);
   /** ID del filtro guardado/preset actualmente aplicado, o null si "manual". */
   const [activeSavedFilterId, setActiveSavedFilterId] = useState<string | null>(null);

   // Rango del trimestre natural actual (yyyy-mm-dd). Se calcula una sola vez por sesión.
   const currentQuarter = useMemo(() => {
     const today = new Date();
     const q = Math.floor(today.getMonth() / 3);
     const startMonth = q * 3;
     const start = new Date(today.getFullYear(), startMonth, 1);
     const end = new Date(today.getFullYear(), startMonth + 3, 0);
     const fmt = (d: Date) =>
       `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
     return { start: fmt(start), end: fmt(end) };
   }, []);

   const filteredRows = useMemo(() => {
     return rows.filter((r) => {
       if (onlyRecurrent && !recurrentTaxIds.has(r.supplierTaxId)) return false;
       if (onlyWithIrpf) {
         const irpf = r.irpfRate ?? 0;
         if (!irpf || irpf <= 0) return false;
       }
       if (onlyCurrentQuarter) {
         if (!r.date || r.date < currentQuarter.start || r.date > currentQuarter.end) return false;
       }
       for (const [col, selected] of Object.entries(columnFilters)) {
         if (!selected || selected.size === 0) continue;
         const v = getColValue(r, col as ColKey);
         const cellKey = v === "" ? EMPTY_VALUE : v;
         if (!selected.has(cellKey)) return false;
       }
       return true;
     });
   }, [rows, columnFilters, onlyRecurrent, onlyWithIrpf, onlyCurrentQuarter, currentQuarter, recurrentTaxIds]);

   const toggleFilterValue = (col: ColKey, value: string) => {
     setColumnFilters((prev) => {
       const current = prev[col] ? new Set(prev[col]) : new Set<string>();
       if (current.has(value)) current.delete(value);
       else current.add(value);
       return { ...prev, [col]: current.size > 0 ? current : undefined };
     });
     setActiveSavedFilterId(null);
   };

   const selectAllForCol = (col: ColKey) => {
     setColumnFilters((prev) => ({ ...prev, [col]: undefined }));
     setActiveSavedFilterId(null);
   };

   const clearAllForCol = (col: ColKey) => {
     setColumnFilters((prev) => ({ ...prev, [col]: new Set<string>() }));
     setActiveSavedFilterId(null);
   };

   const activeExtraToggleCount =
     (onlyRecurrent ? 1 : 0) + (onlyWithIrpf ? 1 : 0) + (onlyCurrentQuarter ? 1 : 0);
   const activeFilterCount = Object.values(columnFilters).filter((s) => s && s.size > 0).length;

   const clearAllFilters = () => {
     setColumnFilters({});
     setFilterSearch({});
     setOnlyRecurrent(false);
     setOnlyWithIrpf(false);
     setOnlyCurrentQuarter(false);
     setActiveSavedFilterId(null);
   };

   // Payload serializable de los filtros activos (para guardar/restaurar).
   const currentFilterPayload = useMemo(() => {
     const columns: Record<string, string[]> = {};
     for (const [col, set] of Object.entries(columnFilters)) {
       if (set && set.size > 0) columns[col] = Array.from(set);
     }
     return {
       columns,
       onlyRecurrent,
       onlyWithIrpf,
       onlyCurrentQuarter,
     };
   }, [columnFilters, onlyRecurrent, onlyWithIrpf, onlyCurrentQuarter]);

   const applySavedFilter = useCallback(
     (filter: { id: string; columns: Record<string, string[]>; onlyRecurrent: boolean; onlyWithIrpf?: boolean; onlyCurrentQuarter?: boolean }) => {
       const next: Partial<Record<ColKey, Set<string>>> = {};
       for (const [col, vals] of Object.entries(filter.columns)) {
         if (vals && vals.length > 0) next[col as ColKey] = new Set(vals);
       }
       setColumnFilters(next);
       setFilterSearch({});
       setOnlyRecurrent(!!filter.onlyRecurrent);
       setOnlyWithIrpf(!!filter.onlyWithIrpf);
       setOnlyCurrentQuarter(!!filter.onlyCurrentQuarter);
       setActiveSavedFilterId(filter.id);
     },
     [],
   );

  const startEdit = (rowKey: string, fileId: string, current: string, field: EditableField, vatLineIndex = 0) => {
    setEditingCell(rowKey);
    setEditingFileId(fileId);
    setEditField(field);
    setEditValue(current);
    setEditingVatLineIndex(vatLineIndex);
  };

  const numericFields: EditableField[] = ["taxableBase", "vatRate", "vatAmount", "irpfRate", "irpfAmount", "totalAmount"];

  const logNumericUpdateFailure = useCallback(
    (
      reason: "missing_handler" | "invalid_vat_line_index",
      field: EditableField,
      fileId: string,
      vatLineIndex?: number,
    ) => {
      console.warn("[VatBook] Numeric update failed", {
        reason,
        field,
        fileId,
        vatLineIndex,
      });
    },
    [],
  );

  const trackExpenseCorrection = useCallback(
    (fileId: string, newAccount: string) => {
      const acc = (newAccount || "").trim();
      if (!acc) return;
      const file = files.find((f) => f.id === fileId);
      if (!file) return;
      const taxId = normalizeNif(file.supplierTaxId || "");
      if (!taxId) return;
      // Skip if the new account is already the supplier's current value (no real change).
      const currentAcc = (file.expenseAccountCode || "").trim();
      if (currentAcc === acc) return;

      const pairKey = `${taxId}::${acc}`;
      if (promptedPairsRef.current.has(pairKey)) return;
      const next = (correctionCountsRef.current.get(pairKey) || 0) + 1;
      correctionCountsRef.current.set(pairKey, next);
      if (next < MEMORIZE_THRESHOLD) return;

      // Threshold reached → ask the user once per pair.
      promptedPairsRef.current.add(pairKey);
      const supplierLabel = file.supplier || taxId;
      const catalogDesc =
        catalog.find((c) => c.account.trim() === acc)?.description || "";

      toast({
        title: "¿Recordar esta cuenta para este proveedor?",
        description: `${supplierLabel} → ${acc}${catalogDesc ? " · " + catalogDesc : ""}. Se aplicará automáticamente en futuras facturas.`,
        action: (
          <Button
            size="sm"
            variant="default"
            onClick={async () => {
              try {
                const { data: userData } = await supabase.auth.getUser();
                const userId = userData?.user?.id;
                if (!userId) return;
                // Insert/upsert with a high use_count (10) so it becomes dominant
                // immediately on the next handleApplyMaster pass.
                await supabase.from("supplier_account_history").upsert(
                  {
                    user_id: userId,
                    company_id: appliedCompany?.id ?? null,
                    tax_id: taxId,
                    expense_account: acc,
                    description: catalogDesc || file.supplierDescription || "",
                    use_count: 10,
                    last_used_at: new Date().toISOString(),
                  } as any,
                  { onConflict: "user_id,company_id,tax_id,expense_account" },
                );
                // Update local cache so other rows in the session see it
                const list = history.get(taxId) || [];
                const existing = list.find((h) => h.account === acc);
                if (existing) {
                  existing.useCount = Math.max(existing.useCount, 10);
                } else {
                  list.unshift({ account: acc, description: catalogDesc, useCount: 10 });
                }
                list.sort((a, b) => b.useCount - a.useCount);
                history.set(taxId, list);
                toast({
                  title: "Cuenta memorizada",
                  description: `${supplierLabel} → ${acc} se aplicará por defecto.`,
                });
              } catch (e) {
                console.warn("[memorize account] failed:", e);
              }
            }}
          >
            Sí, recordar
          </Button>
        ),
      });
    },
    [files, catalog, history, appliedCompany?.id, toast],
  );

  const saveEdit = (overrideValue?: string) => {
    let value = (overrideValue ?? editValue).trim();
    const fileId = editingFileId;
    if (!fileId) { setEditingCell(null); return; }

    // Atajo de cuentas: "410.9" → "41000009" (rellena con ceros hasta la
    // longitud estándar del plan de cuentas de esta empresa).
    if (editField === "expenseAccount" || editField === "supplierAccount") {
      const accLen = inferAccountLength(catalog.map((c) => c.account));
      value = expandAccountShortcut(value, accLen);
    }

    if (editField === "expenseAccount") {
      // Only track when editing the file-level (line 0) account: that's the one
      // that will be auto-applied to future invoices for this supplier.
      if (editingVatLineIndex === 0) {
        trackExpenseCorrection(fileId, value);
      }
      onUpdateExpenseAccount?.(fileId, value, editingVatLineIndex);
    } else if (editField === "supplierAccount") {
      onUpdateSupplierAccount?.(fileId, value);
    } else if (editField === "supplierDescription") {
      onUpdateDescription?.(fileId, value);
    } else if (numericFields.includes(editField)) {
      const numValue = parseNumericLike(value);
      const vatIdx = ["taxableBase", "vatRate", "vatAmount"].includes(editField) ? editingVatLineIndex : undefined;
      if (!onUpdateNumericField) {
        logNumericUpdateFailure("missing_handler", editField, fileId, vatIdx);
      } else if (["taxableBase", "vatRate", "vatAmount"].includes(editField) && (!Number.isInteger(vatIdx) || vatIdx === undefined || vatIdx < 0)) {
        logNumericUpdateFailure("invalid_vat_line_index", editField, fileId, vatIdx);
      } else {
        onUpdateNumericField(fileId, editField as any, numValue, vatIdx);
      }
    } else {
      onUpdateVatBookField?.(fileId, editField as "date" | "supplier" | "supplierTaxId" | "invoiceNumber" | "paymentMethod", value);
    }

    setEditingCell(null);
    setEditingFileId(null);
  };

  const editingKey = (rowKey: string, field: EditableField) =>
    editingCell === rowKey && editField === field;

  // Map EditableField → property name on VatBookRow used to read the previous-row value (Ctrl+D)
  const editFieldToRowProp: Record<EditableField, keyof VatBookRow | null> = {
    date: "date",
    supplier: "supplier",
    supplierTaxId: "supplierTaxId",
    invoiceNumber: "invoiceNumber",
    paymentMethod: "paymentMethod",
    supplierAccount: "supplierAccountCode",
    expenseAccount: "expenseAccountCode",
    taxableBase: "taxableBase",
    vatRate: "vatRate",
    vatAmount: "vatAmount",
    irpfRate: "irpfRate",
    irpfAmount: "irpfAmount",
    totalAmount: "totalAmount",
    supplierDescription: "supplierDescription",
  };

  // Ctrl+D: copy the value from the previous row (in filteredRows) for the currently edited field.
  const handleCopyFromAbove = useCallback(() => {
    if (!editingCell) return;
    const idx = filteredRows.findIndex((r) => buildVatRowKey(r) === editingCell);
    if (idx <= 0) return;
    const prevRow = filteredRows[idx - 1];
    const prop = editFieldToRowProp[editField];
    if (!prop) return;
    const raw = prevRow[prop];
    if (raw === null || raw === undefined) return;
    const str = typeof raw === "number" ? String(raw) : String(raw);
    setEditValue(str);
    saveEdit(str);
  }, [editingCell, filteredRows, editField]);

  const hasLeasingData = useMemo(() =>
    rows.some((r) => r.leasingCostRecovery !== null && r.leasingCostRecovery !== 0), [rows]);

  const toDDMMYYYY = (iso: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || "").trim());
    return m ? `${m[3]}-${m[2]}-${m[1]}` : (iso || "");
  };

  const handleDownloadExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Libro IVA");

    const headers = [
      "Serie/Nº IVA", "Fecha", "Proveedor", "NIF", "Nº Factura",
      "Base Imponible", "Tipo IVA (%)", "Cuota IVA", "Tipo IRPF (%)",
      "Cuota IRPF", "Total Factura", "Forma de Pago", "Cuenta Proveedor",
      "Cuenta de Gasto", "Descripción", "Fecha contable",
    ];
    ws.addRow(headers);
    ws.getRow(1).font = { bold: true };

    // Serie tal cual (sin padding de ceros) y nº de factura tal cual (sin prefijo añadido)
    rows.forEach((r) => {
      const hasCostRecovery = !!(r.leasingCostRecovery && r.leasingCostRecovery !== 0);
      const extraLines: { amount: number; account: string; label: string }[] = [];
      if (r.leasingFinancialCharge && r.leasingFinancialCharge !== 0) {
        extraLines.push({ amount: r.leasingFinancialCharge, account: r.leasingFinancialChargeAccount, label: "Carga financiera" });
      }
      if (r.leasingInsurance && r.leasingInsurance !== 0) {
        extraLines.push({ amount: r.leasingInsurance, account: r.leasingInsuranceAccount, label: "Seguro" });
      }

      const row = [
        r.ivaSeriesNumber, r.date, r.supplier, r.supplierTaxId, r.invoiceNumber,
        hasCostRecovery ? r.leasingCostRecovery : r.taxableBase,
        r.vatRate, r.vatAmount, r.irpfRate ?? "", r.irpfAmount ?? "",
        r.totalAmount ?? "", r.paymentMethod, r.supplierAccountCode,
        hasCostRecovery ? r.leasingCostRecoveryAccount : (r.expenseAccountCode || ""),
        r.supplierDescription, toDDMMYYYY(orgExportSettings.useInvoiceDateAsEntryDate ? r.date : (accountingDate || r.date)),
      ];
      ws.addRow(row);

      extraLines.forEach((l) => {
        ws.addRow([
          r.ivaSeriesNumber, r.date, r.supplier, r.supplierTaxId, r.invoiceNumber,
          l.amount, "", "", "", "",
          "", r.paymentMethod, r.supplierAccountCode,
          l.account, r.supplierDescription, toDDMMYYYY(orgExportSettings.useInvoiceDateAsEntryDate ? r.date : (accountingDate || r.date)),
        ]);
      });
    });

    ws.columns.forEach((col) => { col.width = 16; });

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `Libro_IVA_${today}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);

    // Record account usage history (silent on failure)
    const usageItems = rows
      .filter((r) => r.supplierTaxId && r.expenseAccountCode)
      .map((r) => ({
        taxId: r.supplierTaxId,
        expenseAccount: r.expenseAccountCode,
        description: r.supplierDescription || "",
      }));
    void recordAccountUsage(usageItems, appliedCompany?.id ?? null);

    // Volcado al histórico permanente — pregunta al usuario antes de añadir
    if (appliedCompany?.id) {
      const seen = new Set<string>();
      const historyEntries = files
        .filter((f) => {
          const key = `${f.id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          // invoice_date es opcional en el histórico: no exigir f.date
          return f.supplierTaxId && Number.isFinite(f.totalAmount);
        })
        .map((f) => ({
          company_id: appliedCompany.id,
          supplier_tax_id: f.supplierTaxId || "",
          extracted_tax_id: (f as any).originalExtractedTaxId || null,
          supplier_name: f.supplier || "",
          invoice_number: f.invoiceNumber || null,
          invoice_date: f.date || null,
          base_amount: (f.vatBreakdown ?? []).reduce((s, l) => s + (Number(l.taxableBase) || 0), 0) || null,
          vat_amount: (f.vatBreakdown ?? []).reduce((s, l) => s + (Number(l.vatAmount) || 0), 0) || null,
          total_amount: Number(f.totalAmount ?? 0),
          source: "vat_export" as const,
        }));

      if (historyEntries.length > 0) {
        const decision = typeof window !== "undefined" ? sessionStorage.getItem("history-add-decision") : null;
        if (decision === "always") {
          void insertHistoryEntries(historyEntries).then((res) => {
            const failed = res.errors.length;
            toast({
              title: failed > 0 ? "Histórico actualizado con errores" : "Añadidas al histórico",
              description: `${res.inserted} nuevas · ${res.skipped} ya existían${failed > 0 ? ` · ${failed} con error` : ""}`,
              variant: failed > 0 ? "destructive" : "default",
            });
            if (failed > 0) console.error("[invoice_history] errores:", res.errors);
          });
        } else if (decision !== "never") {
          setPendingHistory({ entries: historyEntries, source: "excel" });
        }
      }
    }
  };

  const handleDownloadA3Eco = () => {
    if (!appliedCompany) {
      toast({
        title: "Aplica primero un maestro",
        description: "Selecciona la empresa para poder generar el fichero SUENLACE.",
        variant: "destructive",
      });
      return;
    }
    setArchivosDialogOpen(true);
  };

  /**
   * Tras una exportación SUENLACE exitosa (uno o varios archivos), encola el volcado
   * al histórico permanente respetando la decisión guardada en sessionStorage.
   */
  const promptHistoryAfterA3Export = useCallback(() => {
    if (!appliedCompany?.id) return;
    const historyEntries = files
      .filter((f) => f.supplierTaxId && Number.isFinite(f.totalAmount))
      .map((f) => ({
        company_id: appliedCompany.id,
        supplier_tax_id: f.supplierTaxId || "",
        extracted_tax_id: (f as any).originalExtractedTaxId || null,
        supplier_name: f.supplier || "",
        invoice_number: f.invoiceNumber || null,
        invoice_date: f.date || null,
        base_amount: (f.vatBreakdown ?? []).reduce((s, l) => s + (Number(l.taxableBase) || 0), 0) || null,
        vat_amount: (f.vatBreakdown ?? []).reduce((s, l) => s + (Number(l.vatAmount) || 0), 0) || null,
        total_amount: Number(f.totalAmount ?? 0),
        source: "vat_export" as const,
      }));
    if (historyEntries.length === 0) return;
    const decision = typeof window !== "undefined" ? sessionStorage.getItem("history-add-decision") : null;
    if (decision === "always") {
      void insertHistoryEntries(historyEntries).then((res) => {
        const failed = res.errors.length;
        toast({
          title: failed > 0 ? "Histórico actualizado con errores" : "Añadidas al histórico",
          description: `${res.inserted} nuevas · ${res.skipped} ya existían${failed > 0 ? ` · ${failed} con error` : ""}`,
          variant: failed > 0 ? "destructive" : "default",
        });
        if (failed > 0) console.error("[invoice_history] errores:", res.errors);
      });
    } else if (decision !== "never") {
      setPendingHistory({ entries: historyEntries, source: "a3" });
    }
  }, [appliedCompany, files, toast]);



  const focusFile = (fileId: string) => {
    setHighlightedFileId(fileId);
    // Scroll to row
    setTimeout(() => {
      const el = document.querySelector(`[data-file-id="${fileId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    // Clear highlight after a few seconds
    setTimeout(() => setHighlightedFileId(null), 3500);
  };


  const handleQuickSupplierCreated = useCallback(
    (created: QuickSupplierCreated) => {
      if (!quickSupplierCtx) return;
      const editingFileId = quickSupplierCtx.fileId;
      // Fusiona en el mapa local del maestro
      setMaster((prev) => {
        const next = new Map(prev || new Map<string, SupplierMasterEntry>());
        next.set(created.taxId, created.entry);
        return next;
      });

      // Normaliza NIF para comparar todas las filas del libro de IVA.
      const normNif = (v: string) =>
        (v || "").replace(/[\s.\-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");
      const targetNif = normNif(created.taxId);
      // Razón social oficial guardada en el maestro (entry.description).
      const masterName = (created.entry.description || "").trim();

      // Recorre TODAS las facturas del libro: la fila en edición y cualquier
      // otra factura con el mismo NIF, para que recojan los datos del nuevo
      // maestro (cuenta proveedor, contrapartida y razón social).
      const affectedFileIds = new Set<string>([editingFileId]);
      files.forEach((f) => {
        if (normNif(f.supplierTaxId || "") === targetNif) {
          affectedFileIds.add(f.id);
        }
      });

      affectedFileIds.forEach((fileId) => {
        onUpdateVatBookField?.(fileId, "supplierTaxId", created.taxId);
        if (masterName) {
          onUpdateVatBookField?.(fileId, "supplier", masterName);
        }
        onUpdateSupplierAccount?.(fileId, created.accountCode);
        if (created.expenseAccount) {
          onUpdateExpenseAccount?.(fileId, created.expenseAccount, 0);
        }
        // La descripción de línea solo se aplica a la fila que disparó el alta,
        // para no pisar descripciones específicas de otras facturas.
        if (fileId === editingFileId && created.description) {
          onUpdateDescription?.(fileId, created.description);
        }
      });

      setQuickSupplierCtx(null);
      setEditingCell(null);

      // Re-aplica el maestro a TODAS las facturas: recarga supplier_master
      // desde la base de datos (ya contiene el nuevo proveedor) y enriquece
      // las facturas con cuenta, contrapartida, leasing, etc. Equivale a
      // visitar el maestro y volver, pero sin salir del libro de IVA.
      if (appliedCompany && onApplyMaster) {
        // Defer para no chocar con los setProcessed lanzados arriba.
        setTimeout(() => onApplyMaster(appliedCompany), 0);
      }
    },
    [
      quickSupplierCtx,
      files,
      appliedCompany,
      onApplyMaster,
      onUpdateVatBookField,
      onUpdateSupplierAccount,
      onUpdateExpenseAccount,
      onUpdateDescription,
    ],
  );

  const handleAccountCreated = useCallback(
    (account: string, description: string) => {
      setCompanyCatalog((prev) => {
        if (prev.some((c) => c.account === account)) return prev;
        return [...prev, { account, description }];
      });
    },
    [],
  );

  const openQuickSupplier = useCallback(
    (fileId: string, taxId: string) => {
      const file = files.find((f) => f.id === fileId);
      setQuickSupplierCtx({
        fileId,
        taxId: taxId || file?.supplierTaxId || "",
        supplier: file?.supplier || "",
      });
    },
    [files],
  );

  if (rows.length === 0) return null;

  const renderEditableCell = (
    rowKey: string,
    fileId: string,
    value: string,
    field: EditableField,
    options?: { showRevisar?: boolean; inputClassName?: string; displayClassName?: string; vatLineIndex?: number }
  ) => {
    if (editingKey(rowKey, field)) {
      const isNumericField = numericFields.includes(field);
      return (
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => saveEdit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              saveEdit();
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setEditingCell(null);
              return;
            }
            // Ctrl+D / Cmd+D → copy value from row above in same column
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") {
              e.preventDefault();
              handleCopyFromAbove();
              return;
            }
          }}
          className={options?.inputClassName || "h-6 w-28 text-xs"}
          inputMode={isNumericField ? "decimal" : undefined}
          autoFocus
        />
      );
    }

    if (value) {
      return (
        <span
          className={options?.displayClassName || "cursor-pointer hover:underline"}
          onClick={() => startEdit(rowKey, fileId, value, field, options?.vatLineIndex)}
        >
          {value}
        </span>
      );
    }

    if (options?.showRevisar) {
      return (
        <span
          className="cursor-pointer rounded bg-accent px-1.5 py-0.5 font-semibold text-accent-foreground"
          onClick={() => startEdit(rowKey, fileId, "", field, options?.vatLineIndex)}
        >
          REVISAR
        </span>
      );
    }

    return (
      <span
        className="cursor-pointer text-muted-foreground hover:underline"
          onClick={() => startEdit(rowKey, fileId, "", field, options?.vatLineIndex)}
      >
        —
      </span>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Libro de IVA
          </h2>
          {saveStatus !== undefined && (
            <SaveIndicator status={saveStatus} lastSavedAt={lastSavedAt ?? null} />
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!orgExportSettings.useInvoiceDateAsEntryDate && (
            <div className="flex items-center gap-1.5">
              <Label htmlFor="accountingDate" className="text-xs text-muted-foreground whitespace-nowrap">
                Fecha contable
              </Label>
              <Input
                id="accountingDate"
                type="date"
                value={accountingDate}
                onChange={(e) => setAccountingDate(e.target.value)}
                className="h-8 w-[140px] text-xs"
              />
            </div>
          )}
          <Button variant="outline" size="sm" onClick={handleDownloadExcel} className="gap-2 shrink-0">
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Descargar </span>Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadA3Eco} className="gap-2 shrink-0" title="Generar fichero SUENLACE para A3ECO">
            <Download className="h-3.5 w-3.5" />
            SUENLACE<span className="hidden sm:inline"> A3ECO</span>
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-2 flex-wrap">
          <SavedFiltersBar
            companyId={appliedCompany?.id || ""}
            currentPayload={currentFilterPayload}
            activeFilterId={activeSavedFilterId}
            onApply={(f) => applySavedFilter(f)}
            onClear={clearAllFilters}
          />
          {(activeFilterCount > 0 || activeExtraToggleCount > 0) && (
            <>
              <Filter className="h-3.5 w-3.5" />
              <span>
                {activeFilterCount + activeExtraToggleCount} filtro(s) activo(s) — {filteredRows.length} de {rows.length} filas
              </span>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={clearAllFilters}>
                <X className="h-3 w-3 mr-1" /> Limpiar todos
              </Button>
            </>
          )}
        </div>
        {recurrentTaxIds.size > 0 && (
          <Button
            variant={onlyRecurrent ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs gap-1.5"
            onClick={() => {
              setOnlyRecurrent((v) => !v);
              setActiveSavedFilterId(null);
            }}
            title="Proveedores con facturas en al menos 3 meses distintos"
          >
            <CheckCircle2 className="h-3 w-3" />
            {onlyRecurrent ? "Mostrando solo recurrentes" : `Solo recurrentes (${recurrentTaxIds.size})`}
          </Button>
        )}
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <Table className="w-full table-fixed">
          <TableHeader>
            <TableRow>
              {([
                { col: "ivaSeriesNumber" as ColKey, label: "Serie/Nº", align: "" },
                { col: "date" as ColKey, label: "Fecha", align: "" },
                { col: "supplier" as ColKey, label: "Proveedor", align: "" },
                { col: "supplierTaxId" as ColKey, label: "NIF", align: "" },
                { col: "invoiceNumber" as ColKey, label: "Nº Factura", align: "" },
                { col: "taxableBase" as ColKey, label: "Base Imp.", align: "text-right" },
                { col: "vatRate" as ColKey, label: "% IVA", align: "text-right" },
                { col: "vatAmount" as ColKey, label: "Cuota IVA", align: "text-right" },
                { col: "irpfRate" as ColKey, label: "% IRPF", align: "text-right" },
                { col: "irpfAmount" as ColKey, label: "Cuota IRPF", align: "text-right" },
                { col: "totalAmount" as ColKey, label: "Total", align: "text-right" },
                { col: "paymentMethod" as ColKey, label: "F. Pago", align: "" },
                { col: "supplierAccountCode" as ColKey, label: "Cta. Proveedor", align: "" },
                { col: "expenseAccountCode" as ColKey, label: "Cta. Gasto", align: "" },
                { col: "supplierDescription" as ColKey, label: "Descripción", align: "" },
                
                ...(hasLeasingData ? [
                  { col: "leasingCostRecovery" as ColKey, label: "Recup. Coste", align: "text-right" },
                  { col: "leasingCostRecoveryAccount" as ColKey, label: "Cta. 524", align: "" },
                  { col: "leasingFinancialCharge" as ColKey, label: "Carga Fin.", align: "text-right" },
                  { col: "leasingFinancialChargeAccount" as ColKey, label: "Cta. 6623", align: "" },
                  { col: "leasingInsurance" as ColKey, label: "Seguro", align: "text-right" },
                  { col: "leasingInsuranceAccount" as ColKey, label: "Cta. 625", align: "" },
                ] : []),
              ] as { col: ColKey; label: string; align: string }[]).map(({ col, label, align }) => {
                const isActive = columnFilters[col] && columnFilters[col]!.size > 0;
                const vals = uniqueValuesForCol[col] || [];
                const colSearch = filterSearch[col] || "";
                const filteredVals = colSearch
                  ? vals.filter((v) => (v === EMPTY_VALUE ? EMPTY_LABEL : v).toLowerCase().includes(colSearch.toLowerCase()))
                  : vals;
                return (
                  <TableHead key={col} className={`text-xs ${align}`}>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${isActive ? "text-primary font-semibold" : ""}`}>
                          {label}
                          <Filter className={`h-3 w-3 ${isActive ? "text-primary" : "text-muted-foreground/50"}`} />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-2" align="start">
                        <div className="space-y-2">
                          <Input
                            placeholder="Buscar..."
                            value={colSearch}
                            onChange={(e) => setFilterSearch((p) => ({ ...p, [col]: e.target.value }))}
                            className="h-7 text-xs"
                          />
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs flex-1" onClick={() => selectAllForCol(col)}>
                              Todos
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs flex-1" onClick={() => clearAllForCol(col)}>
                              Ninguno
                            </Button>
                          </div>
                          <div className="max-h-48 overflow-y-auto space-y-0.5">
                            {filteredVals.map((v) => {
                              const displayLabel = v === EMPTY_VALUE ? EMPTY_LABEL : v;
                              const selected = !columnFilters[col] || columnFilters[col]!.has(v);
                              return (
                                <label key={v} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-muted cursor-pointer text-xs">
                                  <Checkbox
                                    checked={selected}
                                    onCheckedChange={() => {
                                      if (!columnFilters[col]) {
                                        // First click when "all selected": select only this one
                                        const allExcept = new Set(vals.filter((x) => x !== v));
                                        // Actually: uncheck means select all except this
                                        // If currently all shown, clicking unchecks → set filter to all except v
                                        setColumnFilters((prev) => ({ ...prev, [col]: new Set(vals.filter((x) => x !== v)) }));
                                      } else {
                                        toggleFilterValue(col, v);
                                      }
                                    }}
                                    className="h-3.5 w-3.5"
                                  />
                                  <span className="truncate">{displayLabel}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </TableHead>
                );
              })}
              <TableHead className="text-xs w-16">Acc.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.map((r) => {
              const rowKey = buildVatRowKey(r);
              return (
                <TableRow
                  key={rowKey}
                  data-file-id={r.fileId}
                  className={highlightedFileId === r.fileId ? "bg-amber-100/60 dark:bg-amber-900/30 transition-colors" : undefined}
                >
                  <TableCell className="text-xs font-medium">
                    <span className="flex items-center gap-1">
                      {r.ivaSeriesNumber}
                      {files.find((f) => f.id === r.fileId)?.isDuplicate && r.totalAmount !== null ? (
                        <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {renderEditableCell(rowKey, r.fileId, r.date, "date", { inputClassName: "h-6 w-28 text-xs" })}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1.5">
                      {renderEditableCell(rowKey, r.fileId, r.supplier, "supplier", { inputClassName: "h-6 min-w-[10rem] text-xs" })}
                      {r.vatLineIndex === 0 && recurrentTaxIds.has(r.supplierTaxId) && (
                        <Badge
                          variant="secondary"
                          className="h-4 px-1 text-[9px] font-medium uppercase tracking-wide"
                          title="Proveedor recurrente (≥ 3 meses con facturas)"
                        >
                          Rec.
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {renderEditableCell(rowKey, r.fileId, r.supplierTaxId, "supplierTaxId", {
                      inputClassName: "h-6 w-28 text-xs font-mono",
                      displayClassName: "font-mono cursor-pointer hover:underline",
                    })}
                  </TableCell>

                  <TableCell className="text-xs">
                    {renderEditableCell(rowKey, r.fileId, r.invoiceNumber, "invoiceNumber", { inputClassName: "h-6 w-28 text-xs" })}
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {renderEditableCell(rowKey, r.fileId, r.taxableBase ? fmt(r.taxableBase) : "", "taxableBase", {
                      inputClassName: "h-6 w-20 text-xs text-right",
                      displayClassName: "cursor-pointer hover:underline",
                      vatLineIndex: r.vatLineIndex,
                    })}
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {renderEditableCell(rowKey, r.fileId, `${r.vatRate ?? 0}`, "vatRate", {
                      inputClassName: "h-6 w-16 text-xs text-right",
                      displayClassName: "cursor-pointer hover:underline",
                      vatLineIndex: r.vatLineIndex,
                    })}
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {renderEditableCell(rowKey, r.fileId, r.vatAmount ? fmt(r.vatAmount) : "", "vatAmount", {
                      inputClassName: "h-6 w-20 text-xs text-right",
                      displayClassName: "cursor-pointer hover:underline",
                      vatLineIndex: r.vatLineIndex,
                    })}
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {r.irpfRate !== null
                      ? renderEditableCell(rowKey, r.fileId, r.irpfRate > 0 ? `${r.irpfRate}` : "", "irpfRate", {
                          inputClassName: "h-6 w-16 text-xs text-right",
                          displayClassName: "cursor-pointer hover:underline",
                        })
                      : null}
                  </TableCell>
                  <TableCell className="text-xs text-right">
                    {r.irpfAmount !== null
                      ? renderEditableCell(rowKey, r.fileId, r.irpfAmount > 0 ? fmt(r.irpfAmount) : "", "irpfAmount", {
                          inputClassName: "h-6 w-20 text-xs text-right",
                          displayClassName: "cursor-pointer hover:underline",
                        })
                      : null}
                  </TableCell>
                  <TableCell className="text-xs text-right font-medium">
                    {r.totalAmount !== null ? (
                      <span className="flex items-center justify-end gap-1">
                        {renderEditableCell(rowKey, r.fileId, r.totalAmount > 0 ? fmt(r.totalAmount) : "", "totalAmount", {
                          inputClassName: "h-6 w-20 text-xs text-right",
                          displayClassName: `cursor-pointer hover:underline font-medium ${r.totalInconsistency ? "text-destructive" : ""}`,
                        })}
                        <Badge
                          variant={r.totalInconsistency ? "destructive" : "default"}
                          className={`text-[9px] px-1 py-0 shrink-0 ${
                            r.totalInconsistency
                              ? ""
                              : "bg-[hsl(var(--brand-green))] text-white hover:bg-[hsl(152,40%,45%)]"
                          }`}
                          title={r.totalInconsistency ? `Calculado: ${fmt(r.calculatedTotal ?? 0)}€` : "Cuadrado"}
                        >
                          {r.totalInconsistency ? (
                            <AlertTriangle className="h-2.5 w-2.5" />
                          ) : (
                            <CheckCircle2 className="h-2.5 w-2.5" />
                          )}
                        </Badge>
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-xs">
                    {renderEditableCell(rowKey, r.fileId, r.paymentMethod, "paymentMethod", { inputClassName: "h-6 w-24 text-xs" })}
                  </TableCell>
                  <TableCell className="text-xs">
                    {renderEditableCell(rowKey, r.fileId, r.supplierAccountCode, "supplierAccount", {
                      showRevisar: r.totalAmount !== null,
                      inputClassName: "h-6 w-24 text-xs font-mono",
                      displayClassName: "font-mono cursor-pointer hover:underline",
                    })}
                  </TableCell>
                  <TableCell className="text-xs">
                  <ExpenseAccountCombobox
                    value={r.expenseAccountCode}
                    suggestions={buildSuggestions(r.supplierTaxId, r.licensePlate, master, catalog)}
                    history={(history.get(normalizeNif(r.supplierTaxId)) || []).map((h) => {
                      // Override description with catalog's authoritative one when the account exists there
                      const catMatch = catalog.find((c) => c.account === h.account);
                      return catMatch ? { ...h, description: catMatch.description } : h;
                    })}
                    catalog={catalog}
                    onChange={(account, description) => {
                      onUpdateExpenseAccount?.(r.fileId, account, r.vatLineIndex);
                      // Only mirror the description on line 0 (file-level supplier description)
                      if (r.vatLineIndex === 0 && description && !r.supplierDescription) {
                        onUpdateDescription?.(r.fileId, description);
                      }
                    }}
                    placeholder={r.totalAmount !== null ? "REVISAR" : "—"}
                    companyId={appliedCompany?.id ?? null}
                    onAccountCreated={handleAccountCreated}
                  />
                  </TableCell>
                  <TableCell className="text-xs">
                    {renderEditableCell(rowKey, r.fileId, r.supplierDescription, "supplierDescription", {
                      inputClassName: "h-6 min-w-[8rem] text-xs",
                      displayClassName: "cursor-pointer hover:underline",
                    })}
                   </TableCell>
                  {hasLeasingData && (
                    <>
                      <TableCell className="text-xs text-right">{r.leasingCostRecovery !== null && r.leasingCostRecovery !== 0 ? fmt(r.leasingCostRecovery) : ""}</TableCell>
                      <TableCell className="text-xs font-mono">{r.leasingCostRecoveryAccount}</TableCell>
                      <TableCell className="text-xs text-right">{r.leasingFinancialCharge !== null && r.leasingFinancialCharge !== 0 ? fmt(r.leasingFinancialCharge) : ""}</TableCell>
                      <TableCell className="text-xs font-mono">{r.leasingFinancialChargeAccount}</TableCell>
                      <TableCell className="text-xs text-right">{r.leasingInsurance !== null && r.leasingInsurance !== 0 ? fmt(r.leasingInsurance) : ""}</TableCell>
                      <TableCell className="text-xs font-mono">{r.leasingInsuranceAccount}</TableCell>
                    </>
                  )}
                  <TableCell className="text-xs">
                    <span className="flex items-center gap-0.5">
                      {r.vatLineIndex === 0 && (
                        <button
                          onClick={() => onAddVatLine?.(r.fileId)}
                          className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-primary"
                          title="Añadir línea IVA"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {(files.find((f) => f.id === r.fileId)?.vatBreakdown?.length ?? 1) > 1 && (
                        <button
                          onClick={() => onRemoveVatLine?.(r.fileId, r.vatLineIndex)}
                          className="rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          title="Eliminar línea IVA"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>


      {isMounted && (
        <ExportBatchesDialog
          open={archivosDialogOpen}
          onClose={() => setArchivosDialogOpen(false)}
          files={files}
          appliedCompany={appliedCompany ?? null}
          prefix={prefix}
          startNumber={startNumber}
          defaultAccountingDate={accountingDate}
          onExported={() => promptHistoryAfterA3Export()}
        />
      )}


      <AlertDialog open={!!pendingHistory} onOpenChange={(v) => { if (!v) setPendingHistory(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              ¿Añadir al histórico de facturas?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Se acaban de exportar <strong>{pendingHistory?.entries.length ?? 0}</strong> facturas de{" "}
                <strong>{appliedCompany?.name}</strong>.
              </span>
              <span className="block">
                ¿Quieres añadirlas al histórico permanente para el control de duplicados? Si estás haciendo pruebas, di <strong>No</strong>.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (typeof window !== "undefined") sessionStorage.setItem("history-add-decision", "never");
                  setPendingHistory(null);
                  toast({ title: "No se preguntará más en esta sesión", description: "Las próximas exportaciones no se añadirán al histórico." });
                }}
              >
                No, y no preguntar más
              </Button>
            </div>
            <div className="flex gap-2">
              <AlertDialogCancel onClick={() => setPendingHistory(null)}>No</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const entries = pendingHistory?.entries ?? [];
                  setPendingHistory(null);
                  void insertHistoryEntries(entries).then((res) => {
                    const failed = res.errors.length;
                    toast({
                      title: failed > 0 ? "Histórico actualizado con errores" : "Añadidas al histórico",
                      description: `${res.inserted} nuevas · ${res.skipped} ya existían${failed > 0 ? ` · ${failed} con error` : ""}`,
                      variant: failed > 0 ? "destructive" : "default",
                    });
                    if (failed > 0) console.error("[invoice_history] errores:", res.errors);
                  });
                }}
              >
                Sí, añadir
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuickSupplierDialog
        open={!!quickSupplierCtx}
        onClose={() => setQuickSupplierCtx(null)}
        companyId={appliedCompany?.id ?? null}
        defaults={{
          taxId: quickSupplierCtx?.taxId || "",
          name: quickSupplierCtx?.supplier || "",
        }}
        catalog={catalog}
        onCreated={handleQuickSupplierCreated}
      />
    </div>
  );
};

export default VatBook;
